import { ApolloQueryResult } from 'apollo-client';

import { SelectorItems, SelectorItemsDisableReason } from '../../components/CampaignAndGroupSelector/Selector/Selector';
import {
    BANNERS_IN_GROUP_LIMIT,
    INTERNAL_BANNERS_IN_CAMPAIGN_LIMIT,
    INTERNAL_BANNERS_IN_GROUP_LIMIT
} from '../../constants';
import {
    adsPreviewQuery,
    adsQuery,
    calloutsIdsQuery,
    campaignPayActionInfoQuery,
    campaignsQuery,
    copyAdGroupsInfo,
    groupAdQuery,
    groupsMassActionsCount,
    groupsQuery,
    phrasesQuery,
    retargetingsQuery,
    smartFiltersGridQuery,
    usersProfileQuery
} from '../../graphql/queries/grid/domain';
import { freelancerCustomersQuery, freelancerRegisteredQuery } from '../../graphql/queries/grid/freelancers';
import {
    bannersSuggest,
    campaignsSuggest,
    campaignsSuggestList,
    feedFiltersSuggest,
    groupsSuggest,
    groupsSuggestList,
    phrasesSuggest,
    retargetingsSuggest
} from '../../graphql/queries/grid/suggest';
import { mapCalloutsIdsApiFormat, mapRowsToBannersPreviewData } from '../../mappers/banner/banner';
import { mapCampaignTypesQueryResponseToCampaignTypes } from '../../mappers/campaign';
import { mapCampaignRowTypeToCampaignType, mapGroupRowTypeToGroupType } from '../../mappers/common/common';
import { mapLangToGdLanguageInput } from '../../mappers/freelancers';
import { mapDuplicateGroupsApiReponseData, mapRowsToGroupsForCopy } from '../../mappers/group/group';
import { LangUtils } from '../../service/Lang/Lang';
import { RUM, rumCounters } from '../../service/RUM/RUM';
import {
    MapApiSuggestRequestParams,
    SuggestItem,
    YandexMapHelper
} from '../../service/YandexMapHelper/YandexMapHelper';
import {
    banners,
    campaigns,
    ClientInfo,
    feedFilters,
    groups,
    phrases,
    retargetings,
    usersProfile
} from '../../typings/common';
import { BannerPreviewData, CampaignType } from '../../typings/domain';
import { Goal } from '../../typings/goals';
import {
    BannersPreviewQuery,
    BannersQuery,
    BannersSuggestQuery,
    CalloutsIdsQuery,
    CampaignPayActionInfoQuery,
    CampaignsQuery,
    CampaignsSuggestListQuery,
    CampaignsSuggestQuery,
    CopyAdGroupsInfoQuery,
    FeedFiltersSuggestQuery,
    FreelancerCustomersQuery,
    FreelancerRegisteredQuery,
    GdAdGroupOrderByFieldInput,
    GdAdGroupPrimaryStatus,
    GdCampaignOrderByFieldInput,
    GdCampaignTypeInput,
    GdGoalType,
    GdShowConditionTypeInput,
    GdStatPresetInput,
    GroupAdQuery,
    GroupsMassActionsCount,
    GroupsQuery,
    GroupsSuggestListQuery,
    GroupsSuggestQuery,
    OrderInput,
    PhrasesQuery,
    PhrasesSuggestQuery,
    RetargetingsQuery,
    RetargetingsSuggestQuery,
    SmartFiltersGridQuery,
    UsersProfileQuery
} from '../../typings/graphql-grid';
import {
    GridActionFeatures,
    GridData,
    GridParams,
    GridSelectionInfo,
    GridTotalStatsInfo,
    GridViewType,
    PayActionInfo
} from '../../typings/grid';
import { PhrasesFeaturesData } from '../../typings/inspector';
import { RegionSuggestElem } from '../../typings/regions';
import { ClientFeatures } from '../../typings/state';
import { BaseDataProvider } from '../BaseDataProvider/BaseDataProvider';

import {
    mapBannerQueryResultToEntitiesQueryResponse,
    mapBannerSuggestQueryResultToSuggestQueryResponse,
    mapSourceParamsToBannersAndSuggestQueryVariables,
    mapSourceParamsToBannersPreviewQueryVariables
} from './banners';
import {
    mapCampaignQueryResultToEntitiesQueryResponse,
    mapCampaignSuggestQueryResultToSuggestQueryResponse,
    mapSourceParamsToCampaignAndSuggestQueryVariables
} from './campaigns';
import { mapGridParamsToStatRequirements } from './common';
import {
    mapFeedFilterQueryResultToEntitiesQueryResponse,
    mapFeedFilterSuggestQueryResultToSuggestQueryResponse,
    mapSourceParamsToFeedFiltersAndSuggestQueryVariables
} from './feedFilters';
import {
    mapGroupQueryResultToEntitiesQueryResponse,
    mapGroupSuggestQueryResultToSuggestQueryResponse,
    mapSourceParamsToGroupsAndSuggestQueryVariables
} from './groups';
import {
    mapPhraseQueryResultToEntitiesQueryResponse,
    mapPhraseSuggestQueryResultToSuggestQueryResponse,
    mapSourceParamsToPhrasesAndSuggestQueryVariables
} from './phrases';
import {
    mapRetargetingQueryResultToEntitiesQueryResponse,
    mapRetargetingSuggestQueryResultToSuggestQueryResponse,
    mapSourceParamsToRetargetingsAndSuggestQueryVariables,
    mapSourceParamsToRetargetingsQueryVariables
} from './retargetings';
import {
    mapSourceParamsToUserProfileQueryVariables,
    mapSourceParamsToUsersProfileAndSuggestQueryVariables,
    mapUsersProfileQueryResultToEntitiesQueryResponse,
    mapUsersProfileSuggestQueryResultToSuggestQueryResponse
} from './usersProfile';

const LANG = mapLangToGdLanguageInput(LangUtils.getCurrentLang());

export interface EntitiesQueryResponse {
    data: GridData;
    clientInfo: ClientInfo;
    totalStats?: GridTotalStatsInfo;
    hasObjectsOverLimit: boolean;
    reqId: string;
    gridActionFeatures?: GridActionFeatures;
    goals?: Record<string, Goal>;
    phraseContextData?: PhrasesFeaturesData;
}

export interface SuggestQueryResponse {
    data: string[];
}

export interface CampaignsSuggestList {
    id: string;
    name: string;
    type: CampaignType;
}

export interface FetchGroupsSuggestListFilter {
    campaignId: string;
    nameContains: string;
    showConditionTypeIn?: GdShowConditionTypeInput[];
}

export class EntitesDataProvider extends BaseDataProvider {
    public getMapApiSuggestItems(params: MapApiSuggestRequestParams): Promise<SuggestItem[] | RegionSuggestElem[]> {
        return YandexMapHelper.getMapApiSuggestItems(params);
    }

    public fetchFreelancerCustomers(login: string) {
        return this.createQuery<FreelancerCustomersQuery>(freelancerCustomersQuery, { variables: { login } });
    }

    public fetchCampaignsSuggestList(
        login: string,
        filter: string,
        typeIn?: GdCampaignTypeInput[]
    ): Promise<SelectorItems[]> {
        const query = this.createQuery<CampaignsSuggestListQuery>(campaignsSuggestList, {
            variables: {
                login,
                campaignInput: {
                    statRequirements: {
                        preset: GdStatPresetInput.TODAY
                    },
                    // Поумолчанию сортировка ставится по id ASC
                    orderBy: [
                        {
                            field: GdCampaignOrderByFieldInput.ID,
                            order: OrderInput.DESC
                        }
                    ],
                    // Поумолчанию лимит ставится 100
                    limitOffset: {
                        offset: 0,
                        limit: 100
                    },
                    filter: {
                        typeIn: typeIn || null,
                        nameContains: filter || null,
                        archived: false
                    }
                }
            }
        });

        return query.then((result: ApolloQueryResult<CampaignsSuggestListQuery>) =>
            result.data.client.campaigns.rowset.map<SelectorItems>(item => {
                const selectorItem: SelectorItems = {
                    id: String(item.id),
                    name: item.name,
                    type: mapCampaignRowTypeToCampaignType(item.type),
                    isDisabled: false,
                    isAutoBudget: item.strategy.isAutoBudget
                };

                let adsCount;
                let campaignBannersLimit;

                if (item.__typename === 'GdInternalFreeCampaign' || item.__typename === 'GdInternalDistribCampaign') {
                    adsCount = item.adsCount;

                    campaignBannersLimit = INTERNAL_BANNERS_IN_CAMPAIGN_LIMIT;
                }

                if (adsCount !== undefined && campaignBannersLimit !== undefined) {
                    selectorItem.adsCount = adsCount;
                    selectorItem.isDisabled = adsCount >= campaignBannersLimit;
                    selectorItem.disableReason = SelectorItemsDisableReason.REACHED_COUNT_LIMIT;
                }

                return selectorItem;
            })
        );
    }

    public fetchCampaignsSuggestListWithTextTypeFilter(
        login: string,
        filter: string,
        campaignType?: CampaignType
    ): Promise<SelectorItems[]> {
        const query = this.createQuery<CampaignsSuggestListQuery>(campaignsSuggestList, {
            variables: {
                login,
                campaignInput: {
                    statRequirements: {
                        preset: GdStatPresetInput.TODAY
                    },
                    // Поумолчанию сортировка ставится по id ASC
                    orderBy: [
                        {
                            field: GdCampaignOrderByFieldInput.ID,
                            order: OrderInput.DESC
                        }
                    ],
                    // Поумолчанию лимит ставится 100
                    limitOffset: {
                        offset: 0,
                        limit: 100
                    },
                    filter: {
                        nameContains: filter || null,
                        archived: false
                    }
                }
            }
        });

        return query.then((result: ApolloQueryResult<CampaignsSuggestListQuery>) => {
            return mapRowsToGroupsForCopy(result.data.client.campaigns.rowset, campaignType);
        });
    }

    public fetchGroupsSuggestList(login: string, filter: FetchGroupsSuggestListFilter): Promise<SelectorItems[]> {
        const query = this.createQuery<GroupsSuggestListQuery>(groupsSuggestList, {
            variables: {
                login,
                adGroupInput: {
                    statRequirements: {
                        preset: GdStatPresetInput.TODAY
                    },
                    // Поумолчанию сортировка ставится по id ASC
                    orderBy: [
                        {
                            field: GdAdGroupOrderByFieldInput.ID,
                            order: OrderInput.DESC
                        }
                    ],
                    // Поумолчанию лимит ставится 100
                    limitOffset: {
                        offset: 0,
                        limit: 100
                    },
                    filter: {
                        campaignIdIn: [filter.campaignId],
                        showConditionTypeIn: filter.showConditionTypeIn,
                        nameContains: filter.nameContains
                    }
                }
            }
        });

        return query.then((result: ApolloQueryResult<GroupsSuggestListQuery>) =>
            result.data.client.adGroups.rowset.map(
                (item): SelectorItems => {
                    let disableReason: SelectorItemsDisableReason | undefined;

                    if (item.status.primaryStatus === GdAdGroupPrimaryStatus.ARCHIVED) {
                        disableReason = SelectorItemsDisableReason.ARCHIVED;
                    } else if (
                        item.adsCount >=
                        (item.__typename === 'GdInternalAdGroup'
                            ? INTERNAL_BANNERS_IN_GROUP_LIMIT
                            : BANNERS_IN_GROUP_LIMIT)
                    ) {
                        disableReason = SelectorItemsDisableReason.REACHED_COUNT_LIMIT;
                    }

                    return {
                        id: String(item.id),
                        name: item.name,
                        type: mapCampaignRowTypeToCampaignType(item.campaign.type),
                        adsCount: item.adsCount,
                        disableReason: disableReason,
                        isDisabled: disableReason !== undefined,
                        minusKeywords: item.minusKeywords,
                        regionsIds: item.regionsInfo.regionIds,
                        groupType: mapGroupRowTypeToGroupType(item.type)
                    };
                }
            )
        );
    }

    public fetchGridDataSegment = (
        params: GridParams,
        clientInfo: ClientInfo,
        offset: number,
        count: number,
        features: ClientFeatures,
        cacheKey?: string
    ): Promise<EntitiesQueryResponse> => {
        switch (params.view) {
            case campaigns:
                return this.fetchCampaignsGridDataSegment(params, clientInfo, offset, count, features, cacheKey);

            case groups:
                return this.fetchGroupsGridDataSegment(params, clientInfo, offset, count, cacheKey);

            case banners:
                return this.fetchBannersGridDataSegment(params, clientInfo, offset, count, features, cacheKey);

            case phrases:
                return this.fetchPhrasesGridDataSegment(params, clientInfo, offset, count, cacheKey);

            case retargetings:
                return this.fetchRetargetingsGridDataSegment(params, clientInfo, offset, count, cacheKey);

            case feedFilters:
                return this.fetchFeedFiltersGridDataSegment(params, clientInfo, offset, count, cacheKey);

            case usersProfile:
                return this.fetchUsersProfileGridDataSegment(params, clientInfo, offset, count, cacheKey);

            default:
                throw new Error('unexpected view type');
        }
    };

    public fetchSpecificGridDataItems = (
        params: GridParams,
        view: GridViewType,
        selectionInfo: GridSelectionInfo
    ): Promise<EntitiesQueryResponse> => {
        switch (view) {
            case campaigns:
                return this.fetchCampaignsGridDataItems(params, selectionInfo);
            case banners:
                return this.fetchBannersGridDataItems(params, selectionInfo);
            case phrases:
                return this.fetchPhrasesGridDataItems(params, selectionInfo);
            default:
                throw new Error(`this view type is not supported yet ${params.view}`);
        }
    };

    public fetchSuggestData = (
        sourceParams: GridParams,
        clientInfo: ClientInfo,
        features: ClientFeatures,
        cacheKey?: string
    ): Promise<SuggestQueryResponse> => {
        switch (sourceParams.view) {
            case campaigns:
                return this.fetchCampaignsSuggestData(sourceParams, clientInfo, features, cacheKey);

            case groups:
                return this.fetchGroupsSuggestData(sourceParams, clientInfo, cacheKey);

            case banners:
                return this.fetchBannersSuggestData(sourceParams, clientInfo, features, cacheKey);

            case phrases:
                return this.fetchPhrasesSuggestData(sourceParams, clientInfo, cacheKey);

            case retargetings:
                return this.fetchRetargetingsSuggestData(sourceParams, clientInfo, cacheKey);

            case feedFilters:
                return this.fetchFeedFiltersSuggestData(sourceParams, clientInfo, cacheKey);

            case usersProfile:
                return this.fetchUsersProfileSuggestData(sourceParams, clientInfo, cacheKey);

            default:
                throw new Error('unexpected view type');
        }
    };

    public fetchFreelancerRegisteredData(freelancerLogin: string, login: string) {
        return this.createQuery<FreelancerRegisteredQuery>(freelancerRegisteredQuery, {
            variables: {
                freelancerLogin,
                login,
                lang: LANG
            }
        }).then(response => response.data);
    }

    public fetchGridGroupsCounts = (params: GridParams, clientInfo: ClientInfo, cacheKey?: string) => {
        const query = this.createQuery<GroupsMassActionsCount>(
            groupsMassActionsCount,
            {
                variables: {
                    login: params.login,
                    adGroupInput: {
                        ...mapSourceParamsToGroupsAndSuggestQueryVariables(params, clientInfo),
                        limitOffset: {
                            offset: 0,
                            limit: 10000
                        },
                        cacheKey: cacheKey || null
                    }
                }
            },
            rumCounters.backendGetGroupsMassActionsCount
        );

        return query.then((result: ApolloQueryResult<GroupsMassActionsCount>) => result.data.client.adGroups.features);
    };

    public getGroupsForDuplicateAction(
        login: string,
        campaignsIds: string[],
        groupsIds: string[],
        groupsCache?: string
    ): Promise<object> {
        const query = this.createQuery<CopyAdGroupsInfoQuery>(copyAdGroupsInfo, {
            variables: {
                login: login,
                adGroupInput: {
                    statRequirements: {
                        preset: GdStatPresetInput.TODAY
                    },
                    filter: {
                        campaignIdIn: campaignsIds,
                        adGroupIdIn: groupsIds
                    },
                    limitOffset: {
                        offset: 0,
                        limit: 1000
                    },
                    orderBy: [
                        {
                            order: OrderInput.DESC,
                            field: GdAdGroupOrderByFieldInput.CAMPAIGN_ID
                        },
                        {
                            order: OrderInput.DESC,
                            field: GdAdGroupOrderByFieldInput.ID
                        }
                    ],
                    cacheKey: groupsCache || null
                }
            }
        });

        return query.then((result: ApolloQueryResult<CopyAdGroupsInfoQuery>) =>
            mapDuplicateGroupsApiReponseData(result.data)
        );
    }

    public getBannersPreview(
        params: GridParams,
        clientInfo: ClientInfo,
        features: ClientFeatures,
        campaignsIds: string[],
        groupsIds: string[]
    ): Promise<BannerPreviewData[]> {
        const additionalParams: GridParams = {
            ...params,
            groupsIds
        };

        const query = this.createQuery<BannersPreviewQuery>(
            adsPreviewQuery,
            {
                variables: {
                    login: params.login,
                    adsInput: {
                        ...mapSourceParamsToBannersPreviewQueryVariables(
                            {
                                ...additionalParams,
                                statPeriod: {
                                    ...additionalParams.statPeriod
                                }
                            },
                            clientInfo,
                            features
                        ),
                        filter: {
                            campaignIdIn: campaignsIds,
                            adGroupIdIn: groupsIds
                        },
                        limitOffset: {
                            offset: 0,
                            limit: 100
                        }
                    }
                }
            },
            rumCounters.backendGetBanners
        );

        return query.then((result: ApolloQueryResult<BannersPreviewQuery>) =>
            RUM.reportSyncDelta(rumCounters.clientGetBannersMapper, result.data.reqId, () => {
                const { client } = result.data;

                return mapRowsToBannersPreviewData(
                    client.ads.rowset,
                    {
                        chiefLogin: client.chiefLogin
                    },
                    params
                );
            })
        );
    }

    public getGroupBanner(
        login: string,
        campaignId: string,
        groupsId: string
    ): Promise<{ id: string; calloutsIds: Array<string> }> {
        const query = this.createQuery<GroupAdQuery>(groupAdQuery, {
            variables: {
                login: login,
                adsInput: {
                    statRequirements: { preset: GdStatPresetInput.TODAY },
                    orderBy: [],
                    filter: {
                        campaignIdIn: campaignId,
                        adGroupIdIn: groupsId
                    },
                    limitOffset: {
                        offset: 0,
                        limit: 1
                    }
                },
                calloutsFilter: {
                    deleted: false
                }
            }
        });

        return query.then((result: ApolloQueryResult<GroupAdQuery>) => ({
            ...result.data.client.ads.rowset[0],
            calloutsIds: mapCalloutsIdsApiFormat(result)
        }));
    }

    public getCalloutsIds = (login: string): Promise<Array<string>> => {
        const query = this.createQuery<CalloutsIdsQuery>(calloutsIdsQuery, {
            variables: {
                login,
                calloutsFilter: {
                    deleted: false
                }
            }
        });

        return query.then(mapCalloutsIdsApiFormat);
    };

    public fetchPayActionInfo(login: string, campaignIds: string[]): Promise<PayActionInfo> {
        return this.createQuery<CampaignPayActionInfoQuery>(campaignPayActionInfoQuery, {
            variables: {
                login,
                campaignInput: {
                    filter: {
                        campaignIdIn: campaignIds
                    },
                    orderBy: [],
                    statRequirements: { preset: 'TODAY' }, // сейчас это обязательное поле
                    limitOffset: {
                        offset: 0,
                        limit: 10000
                    }
                }
            }
        }).then(response => mapCampaignTypesQueryResponseToCampaignTypes(response.data));
    }

    private fetchCampaignsSuggestData = (
        params: GridParams,
        clientInfo: ClientInfo,
        features: ClientFeatures,
        cacheKey?: string
    ) => {
        const query = this.createQuery<CampaignsSuggestQuery>(
            campaignsSuggest,
            {
                variables: {
                    login: params.login,
                    campaignInput: {
                        ...mapSourceParamsToCampaignAndSuggestQueryVariables(params, clientInfo, features),
                        limitOffset: {
                            offset: 0,
                            limit: 10000
                        },
                        cacheKey: cacheKey || null
                    }
                }
            },
            RUM.getBackendSuggestLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<CampaignsSuggestQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperSuggestLoadKey(params.view), result.data.reqId, () =>
                mapCampaignSuggestQueryResultToSuggestQueryResponse(result)
            )
        );
    };

    private fetchGroupsSuggestData = (params: GridParams, clientInfo: ClientInfo, cacheKey?: string) => {
        const query = this.createQuery<GroupsSuggestQuery>(
            groupsSuggest,
            {
                variables: {
                    login: params.login,
                    adGroupInput: {
                        ...mapSourceParamsToGroupsAndSuggestQueryVariables(params, clientInfo),
                        limitOffset: {
                            offset: 0,
                            limit: 10000
                        },
                        cacheKey: cacheKey || null
                    }
                }
            },
            RUM.getBackendSuggestLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<GroupsSuggestQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperSuggestLoadKey(params.view), result.data.reqId, () =>
                mapGroupSuggestQueryResultToSuggestQueryResponse(result)
            )
        );
    };

    private fetchBannersSuggestData = (
        params: GridParams,
        clientInfo: ClientInfo,
        features: ClientFeatures,
        cacheKey?: string
    ) => {
        const query = this.createQuery<BannersSuggestQuery>(
            bannersSuggest,
            {
                variables: {
                    login: params.login,
                    adsInput: {
                        ...mapSourceParamsToBannersAndSuggestQueryVariables(params, clientInfo, features),
                        limitOffset: {
                            offset: 0,
                            limit: 10000
                        },
                        cacheKey: cacheKey || null
                    }
                }
            },
            RUM.getBackendSuggestLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<BannersSuggestQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperSuggestLoadKey(params.view), result.data.reqId, () =>
                mapBannerSuggestQueryResultToSuggestQueryResponse(result)
            )
        );
    };

    private fetchPhrasesSuggestData = (params: GridParams, clientInfo: ClientInfo, cacheKey?: string) => {
        const query = this.createQuery<PhrasesSuggestQuery>(
            phrasesSuggest,
            {
                variables: {
                    showConditionsInput: {
                        limitOffset: {
                            offset: 0,
                            limit: 10000
                        },
                        ...mapSourceParamsToPhrasesAndSuggestQueryVariables(params, clientInfo),
                        cacheKey: cacheKey || null
                    },
                    login: params.login
                }
            },
            RUM.getBackendSuggestLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<PhrasesSuggestQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperSuggestLoadKey(params.view), result.data.reqId, () =>
                mapPhraseSuggestQueryResultToSuggestQueryResponse(result)
            )
        );
    };

    private fetchRetargetingsSuggestData = (params: GridParams, clientInfo: ClientInfo, cacheKey?: string) => {
        const query = this.createQuery<RetargetingsSuggestQuery>(
            retargetingsSuggest,
            {
                variables: {
                    retargetingsInput: {
                        limitOffset: {
                            offset: 0,
                            limit: 10000
                        },
                        ...mapSourceParamsToRetargetingsAndSuggestQueryVariables(params),
                        cacheKey: cacheKey || null
                    },
                    login: params.login
                }
            },
            RUM.getBackendSuggestLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<RetargetingsSuggestQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperSuggestLoadKey(params.view), result.data.reqId, () =>
                mapRetargetingSuggestQueryResultToSuggestQueryResponse(result)
            )
        );
    };

    private fetchUsersProfileSuggestData = (params: GridParams, clientInfo: ClientInfo, cacheKey?: string) => {
        const query = this.createQuery<RetargetingsSuggestQuery>(
            retargetingsSuggest,
            {
                variables: {
                    retargetingsInput: {
                        limitOffset: {
                            offset: 0,
                            limit: 10000
                        },
                        ...mapSourceParamsToUsersProfileAndSuggestQueryVariables(params),
                        cacheKey: cacheKey || null
                    },
                    login: params.login
                }
            },
            RUM.getBackendSuggestLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<RetargetingsSuggestQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperSuggestLoadKey(params.view), result.data.reqId, () =>
                mapUsersProfileSuggestQueryResultToSuggestQueryResponse(result)
            )
        );
    };

    private fetchFeedFiltersSuggestData = (params: GridParams, clientInfo: ClientInfo, cacheKey?: string) => {
        const query = this.createQuery<FeedFiltersSuggestQuery>(
            feedFiltersSuggest,
            {
                variables: {
                    smartFiltersInput: {
                        limitOffset: {
                            offset: 0,
                            limit: 10000
                        },
                        ...mapSourceParamsToFeedFiltersAndSuggestQueryVariables(params),
                        cacheKey: cacheKey || null
                    },
                    login: params.login
                }
            },
            RUM.getBackendSuggestLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<FeedFiltersSuggestQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperSuggestLoadKey(params.view), result.data.reqId, () =>
                mapFeedFilterSuggestQueryResultToSuggestQueryResponse(result)
            )
        );
    };

    private fetchCampaignsGridDataSegment = (
        params: GridParams,
        clientInfo: ClientInfo,
        offset: number,
        count: number,
        features: ClientFeatures,
        cacheKey?: string
    ): Promise<EntitiesQueryResponse> => {
        const query = this.createQuery<CampaignsQuery>(
            campaignsQuery,
            {
                variables: {
                    login: params.login,
                    campaignInput: {
                        ...mapSourceParamsToCampaignAndSuggestQueryVariables(params, clientInfo, features),
                        limitOffset: {
                            offset,
                            limit: count
                        },
                        cacheKey: cacheKey || null
                    }
                }
            },
            RUM.getBackendLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<CampaignsQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperLoadKey(params.view), result.data.reqId, () =>
                mapCampaignQueryResultToEntitiesQueryResponse(result, params)
            )
        );
    };

    private fetchGroupsGridDataSegment = (
        params: GridParams,
        clientInfo: ClientInfo,
        offset: number,
        count: number,
        cacheKey?: string
    ) => {
        const query = this.createQuery<GroupsQuery>(
            groupsQuery,
            {
                variables: {
                    login: params.login,
                    adGroupInput: {
                        ...mapSourceParamsToGroupsAndSuggestQueryVariables(params, clientInfo),
                        limitOffset: {
                            offset,
                            limit: count
                        },
                        cacheKey: cacheKey || null
                    }
                }
            },
            RUM.getBackendLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<GroupsQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperLoadKey(params.view), result.data.reqId, () =>
                mapGroupQueryResultToEntitiesQueryResponse(result, params)
            )
        );
    };

    private fetchBannersGridDataSegment = (
        params: GridParams,
        clientInfo: ClientInfo,
        offset: number,
        count: number,
        features: ClientFeatures,
        cacheKey?: string
    ) => {
        const query = this.createQuery<BannersQuery>(
            adsQuery,
            {
                variables: {
                    login: params.login,
                    adsInput: {
                        ...mapSourceParamsToBannersAndSuggestQueryVariables(params, clientInfo, features),
                        limitOffset: {
                            offset: offset,
                            limit: count
                        },
                        cacheKey: cacheKey || null
                    },
                    placementsInput: {
                        placementType: 'OUTDOOR'
                    }
                }
            },
            RUM.getBackendLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<BannersQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperLoadKey(params.view), result.data.reqId, () =>
                mapBannerQueryResultToEntitiesQueryResponse(result, params)
            )
        );
    };

    private fetchPhrasesGridDataSegment = (
        params: GridParams,
        clientInfo: ClientInfo,
        offset: number,
        limit: number,
        cacheKey?: string
    ) => {
        const query = this.createQuery<PhrasesQuery>(
            phrasesQuery,
            {
                variables: {
                    showConditionsInput: {
                        limitOffset: {
                            offset,
                            limit
                        },
                        ...mapSourceParamsToPhrasesAndSuggestQueryVariables(params, clientInfo),
                        cacheKey: cacheKey || null
                    },
                    login: params.login
                }
            },
            RUM.getBackendLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<PhrasesQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperLoadKey(params.view), result.data.reqId, () =>
                mapPhraseQueryResultToEntitiesQueryResponse(result, params)
            )
        );
    };

    private fetchRetargetingsGridDataSegment = (
        params: GridParams,
        clientInfo: ClientInfo,
        offset: number,
        count: number,
        cacheKey?: string
    ) => {
        const query = this.createQuery<RetargetingsQuery>(
            retargetingsQuery,
            {
                variables: {
                    retargetingsInput: {
                        ...mapSourceParamsToRetargetingsQueryVariables(params),
                        limitOffset: {
                            offset,
                            limit: count
                        },
                        cacheKey: cacheKey || null
                    },
                    goalsInput: {
                        filter: {
                            goalTypeIn: [GdGoalType.ECOMMERCE, GdGoalType.SEGMENT, GdGoalType.AUDIENCE, GdGoalType.GOAL]
                        }
                    },
                    login: params.login
                }
            },
            RUM.getBackendLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<RetargetingsQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperLoadKey(params.view), result.data.reqId, () =>
                mapRetargetingQueryResultToEntitiesQueryResponse(result, params)
            )
        );
    };

    private fetchFeedFiltersGridDataSegment = (
        params: GridParams,
        clientInfo: ClientInfo,
        offset: number,
        limit: number,
        cacheKey?: string
    ) => {
        const query = this.createQuery<SmartFiltersGridQuery>(
            smartFiltersGridQuery,
            {
                variables: {
                    smartFiltersInput: {
                        limitOffset: {
                            offset,
                            limit
                        },
                        ...mapSourceParamsToFeedFiltersAndSuggestQueryVariables(params),
                        cacheKey: cacheKey || null
                    },
                    login: params.login
                }
            },
            RUM.getBackendLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<SmartFiltersGridQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperLoadKey(params.view), result.data.reqId, () =>
                mapFeedFilterQueryResultToEntitiesQueryResponse(result, params)
            )
        );
    };

    private fetchUsersProfileGridDataSegment = (
        params: GridParams,
        clientInfo: ClientInfo,
        offset: number,
        count: number,
        cacheKey?: string
    ) => {
        const query = this.createQuery<UsersProfileQuery>(
            usersProfileQuery,
            {
                variables: {
                    retargetingsInput: {
                        ...mapSourceParamsToUserProfileQueryVariables(params),
                        limitOffset: {
                            offset,
                            limit: count
                        },
                        cacheKey: cacheKey || null
                    },
                    goalsInput: {
                        filter: {
                            goalTypeIn: [GdGoalType.ECOMMERCE, GdGoalType.SEGMENT, GdGoalType.AUDIENCE, GdGoalType.GOAL]
                        }
                    },
                    login: params.login
                }
            },
            RUM.getBackendLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<UsersProfileQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperLoadKey(params.view), result.data.reqId, () =>
                mapUsersProfileQueryResultToEntitiesQueryResponse(result, params)
            )
        );
    };

    private fetchCampaignsGridDataItems = (
        params: GridParams,
        selectionInfo: GridSelectionInfo
    ): Promise<EntitiesQueryResponse> => {
        const query = this.createQuery<CampaignsQuery>(
            campaignsQuery,
            {
                variables: {
                    login: params.login,
                    campaignInput: {
                        filter: {
                            campaignIdIn: selectionInfo.campaigns
                        },
                        statRequirements: mapGridParamsToStatRequirements(params),
                        limitOffset: {
                            offset: 0,
                            limit: selectionInfo.campaigns.length
                        },
                        orderBy: [],
                        cacheKey: null
                    }
                }
            },
            RUM.getBackendLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<CampaignsQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperLoadKey(params.view), result.data.reqId, () =>
                mapCampaignQueryResultToEntitiesQueryResponse(result, params)
            )
        );
    };

    private fetchBannersGridDataItems = (params: GridParams, selectionInfo: GridSelectionInfo) => {
        const query = this.createQuery<BannersQuery>(
            adsQuery,
            {
                variables: {
                    login: params.login,
                    adsInput: {
                        filter: {
                            campaignIdIn: params.campaignsIds,
                            adIdIn: selectionInfo.banners
                        },
                        statRequirements: mapGridParamsToStatRequirements(params),
                        limitOffset: {
                            offset: 0,
                            limit: selectionInfo.banners.length
                        },
                        orderBy: [],
                        cacheKey: null
                    },
                    placementsInput: {
                        placementType: 'OUTDOOR'
                    }
                }
            },
            RUM.getBackendLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<BannersQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperLoadKey(params.view), result.data.reqId, () =>
                mapBannerQueryResultToEntitiesQueryResponse(result, params)
            )
        );
    };

    private fetchPhrasesGridDataItems = (params: GridParams, selectionInfo: GridSelectionInfo) => {
        const query = this.createQuery<PhrasesQuery>(
            phrasesQuery,
            {
                variables: {
                    showConditionsInput: {
                        filter: {
                            campaignIdIn: params.campaignsIds,
                            showConditionIdIn: selectionInfo.phrases
                        },
                        limitOffset: {
                            offset: 0,
                            limit: selectionInfo.phrases.length
                        },
                        statRequirements: mapGridParamsToStatRequirements(params),
                        orderBy: [],
                        cacheKey: null
                    },
                    login: params.login
                }
            },
            RUM.getBackendLoadKey(params.view)
        );

        return query.then((result: ApolloQueryResult<PhrasesQuery>) =>
            RUM.reportSyncDelta(RUM.getClientMapperLoadKey(params.view), result.data.reqId, () =>
                mapPhraseQueryResultToEntitiesQueryResponse(result, params)
            )
        );
    };
}
